/**
 * @fileoverview ExternalSettingsLink — a NEW-TAB link from a form or wizard to a
 *   Settings / Config destination.
 * @package @puffin-core/feedback-ui
 *
 * PLN-LB-UX-005 B2 (2026-09-02). The rule it encodes (sales R-4): a link that
 * would leave an unsaved draft opens in a NEW tab, and `rel="noreferrer"` is
 * mandatory beside `target="_blank"`. Before this atom the same anchor was
 * hand-rolled twice in planner-web alone (the labor-budget worksheet's gated
 * config link and the sales day-spread section's ungated settings link), with
 * different classes and no shared contract.
 *
 * PLN-DEPT-GRAIN-004 1a (owner ruling R-DS-7, 2026-09-30): the link SAYS it
 * leaves the page. After its label it draws the `ExternalLink` glyph
 * (aria-hidden, `.external-link-glyph`) for the sighted reader and an sr-only
 * "(opens in a new tab)" for the screen reader — so its accessible name is
 * "<label> (opens in a new tab)". The sales-plan wizard's "Group departments"
 * carried neither, and the owner's UAT could not tell it was an action in
 * Settings rather than a note. Both marks live HERE, once: a consumer that
 * dresses the anchor as an action button (`<InlineFeedback actions[].href>`)
 * gets them for free.
 *
 * Deliberately UNGATED: this package cannot know an app's permission
 * vocabulary, so the app wraps it (planner's `PlannerSettingsLink` applies the
 * `PLANNER.CONFIG_MANAGE` gate and returns null without it — a dead-end link
 * helps nobody). It also renders NO leading separator: the caller owns the
 * sentence spacing around it.
 */

import PropTypes from 'prop-types';

import { useFeedbackUiTranslate } from '../hooks/useFeedbackUiTranslate.js';
import { resolveLucideIcon } from '@puffin-core/icon-registry';

/** The glyph's size, in px — sat on the baseline of a 14px label (`.external-link-glyph`). */
const GLYPH_SIZE = 12;

/**
 * @param {Object} props
 * @param {string} props.href - The destination (a registry route, never a literal)
 * @param {React.ReactNode} props.children - The link text
 * @param {string} props.testId - `data-testid` on the anchor
 * @param {string} [props.className='text-link'] - The anchor's class
 * @returns {React.JSX.Element}
 */
export function ExternalSettingsLink({ href, children, testId, className = 'text-link' }) {
  const t = useFeedbackUiTranslate();
  return (
    <a href={href} target="_blank" rel="noreferrer" className={className} data-testid={testId}>
      {children}
      <span className="external-link-glyph" aria-hidden="true">
        {resolveLucideIcon('ExternalLink', { size: GLYPH_SIZE })}
      </span>
      <span className="sr-only"> {t('opensInNewTab')}</span>
    </a>
  );
}

ExternalSettingsLink.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  testId: PropTypes.string.isRequired,
  className: PropTypes.string,
};

export default ExternalSettingsLink;
